import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Clock, AlertCircle, Briefcase, Calendar, Users, Building, TrendingUp, X } from 'lucide-react';
import ExecutiveInfoModal from '../components/ExecutiveInfoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

// Client List Modal Component
const ClientListModal = ({ executive, onClose }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/executives/${executive.id}/clients`, {
          headers: { 'x-admin-key': ADMIN_KEY }
        });
        if (res.ok) {
          const data = await res.json();
          setClients(data.clients || []);
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [executive.id]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          padding: '24px',
          color: '#fff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                {executive.full_name}'s Clients
              </h2>
              <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '14px' }}>
                {clients.length} client{clients.length !== 1 ? 's' : ''} assigned
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={18} color="#fff" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', maxHeight: 'calc(80vh - 100px)', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading clients...
            </div>
          ) : clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Users size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
              <p style={{ color: '#6B7280', margin: 0 }}>No clients assigned yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {clients.map(client => (
                <div
                  key={client.client_id}
                  style={{
                    padding: '16px',
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#1F2937', fontSize: '15px' }}>
                        {client.full_name || client.client_name || 'Unknown'}
                      </p>
                      <p style={{ margin: '2px 0 0', color: '#6B7280', fontSize: '13px' }}>
                        {client.business_name || 'No business name'}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      background: client.status === 'active' ? '#ECFDF5' : '#F3F4F6',
                      color: client.status === 'active' ? '#059669' : '#6B7280',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {client.status || 'New'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#6B7280' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Building size={14} />
                      {client.client_id}
                    </span>
                    {client.executive_assigned_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={14} />
                        Assigned {new Date(client.executive_assigned_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExecutiveHub = () => {
  const navigate = useNavigate();
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [clientsModalExec, setClientsModalExec] = useState(null);

  useEffect(() => {
    fetchExecutives();
  }, []);

  const fetchExecutives = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setExecutives(data.executives || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching executives:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading executives...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/a-dashboard')}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={18} color="#6B7280" />
          </button>
          <div>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Executive Hub
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              View details and preferences for your assigned executives
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        {executives.length === 0 ? (
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            padding: '48px',
            textAlign: 'center'
          }}>
            <AlertCircle size={48} color="#9CA3AF" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
              No Executives Assigned
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              You don't have any executives assigned yet. Contact your administrator.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
            {executives.map(exec => (
              <div
                key={exec.id}
                onClick={() => setSelectedExecutive(exec)}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3B82F6';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header with solid brand blue */}
                <div style={{
                  background: '#3B82F6',
                  padding: '24px',
                  textAlign: 'center'
                }}>
                  {exec.profile_photo_url ? (
                    <img
                      src={exec.profile_photo_url}
                      alt={exec.full_name}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        border: '4px solid rgba(255,255,255,0.3)',
                        marginBottom: '12px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: '4px solid rgba(255,255,255,0.3)',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                      fontSize: '32px',
                      fontWeight: '600',
                      color: '#FFFFFF'
                    }}>
                      {exec.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  )}
                  <h2 style={{ color: '#FFFFFF', fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0' }}>
                    {exec.full_name}
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', margin: '0 0 12px 0' }}>
                    {exec.title}
                  </p>
                  {/* View Clients Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setClientsModalExec(exec);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(255,255,255,0.2)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }}
                  >
                    <Users size={14} />
                    View Clients
                  </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px' }}>
                  {/* Contact Info */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Contact Information
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {exec.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={16} color="#9CA3AF" />
                          <a href={`mailto:${exec.email}`} style={{ color: '#3B82F6', fontSize: '14px', textDecoration: 'none' }}>
                            {exec.email}
                          </a>
                        </div>
                      )}
                      {exec.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={16} color="#9CA3AF" />
                          <a href={`tel:${exec.phone}`} style={{ color: '#3B82F6', fontSize: '14px', textDecoration: 'none' }}>
                            {exec.phone}
                          </a>
                        </div>
                      )}
                      {exec.work_hours && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Clock size={16} color="#9CA3AF" />
                          <span style={{ color: '#6B7280', fontSize: '14px' }}>{exec.work_hours}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preferences */}
                  {exec.preferred_contact_method && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Preferred Contact
                      </h3>
                      <div style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {exec.preferred_contact_method}
                      </div>
                    </div>
                  )}

                  {/* Notes for Assistant */}
                  {exec.notes_for_assistant && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Notes
                      </h3>
                      <div style={{
                        background: '#FEF3C7',
                        border: '1px solid #FDE68A',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '14px',
                        color: '#92400E',
                        lineHeight: '1.5'
                      }}>
                        {exec.notes_for_assistant}
                      </div>
                    </div>
                  )}

                  {/* Travel Preferences */}
                  {exec.travel_preferences_json && Object.keys(exec.travel_preferences_json).length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Briefcase size={14} />
                        Travel Preferences
                      </h3>
                      <div style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        {Object.entries(exec.travel_preferences_json).map(([key, value]) => (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                            <span style={{ color: '#6B7280', fontWeight: '500' }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ color: '#374151' }}>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Meeting Preferences */}
                  {exec.meeting_preferences_json && Object.keys(exec.meeting_preferences_json).length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        Meeting Preferences
                      </h3>
                      <div style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        {Object.entries(exec.meeting_preferences_json).map(([key, value]) => (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                            <span style={{ color: '#6B7280', fontWeight: '500' }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ color: '#374151' }}>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Important Dates */}
                  {exec.important_dates_json && Object.keys(exec.important_dates_json).length > 0 && (
                    <div>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Important Dates
                      </h3>
                      <div style={{
                        background: '#EFF6FF',
                        border: '1px solid #DBEAFE',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        {Object.entries(exec.important_dates_json).map(([key, value]) => (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                            <span style={{ color: '#1E40AF', fontWeight: '500' }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ color: '#1E3A8A' }}>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Emergency Policy */}
                  {exec.emergency_contact_policy && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Emergency Contact Policy
                      </h3>
                      <div style={{
                        background: '#FEF2F2',
                        border: '1px solid #FCA5A5',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '14px',
                        color: '#991B1B',
                        lineHeight: '1.5'
                      }}>
                        {exec.emergency_contact_policy}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Executive Info Modal */}
      {selectedExecutive && (
        <ExecutiveInfoModal
          executive={selectedExecutive}
          onClose={() => setSelectedExecutive(null)}
        />
      )}

      {/* Clients List Modal */}
      {clientsModalExec && (
        <ClientListModal
          executive={clientsModalExec}
          onClose={() => setClientsModalExec(null)}
        />
      )}
    </div>
  );
};

export default ExecutiveHub;
